import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import moment from "moment";
import { isArray } from "lodash";

import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Pagination,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  LinearProgress,
  alpha,
  createFilterOptions,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme as useThemeV5
} from "@mui/material";

import {
  Facebook,
  Instagram,
  WhatsApp,
  SaveAlt,
  FilterList,
  History,
  Forward,
  Visibility,
  Search
} from "@mui/icons-material";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";

// Custom Filters (Assuming they accept standard props or can be wrapped)
// Keeping legacy imports if they are strict about their internal theme usage,
// but they are just Selects usually.
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const Reports = ({ isEmbedded }) => {
  const history = useHistory();
  const { getReport } = useDashboard();
  const { user } = useContext(AuthContext);

  // Theme Bridge
  const themeV4 = useThemeV4();
  const theme = useMemo(() => createTheme({
    palette: {
      mode: themeV4.palette.type,
      primary: themeV4.palette.primary,
      secondary: themeV4.palette.secondary,
      error: themeV4.palette.error,
      warning: themeV4.palette.warning,
      info: themeV4.palette.info,
      success: themeV4.palette.success,
      text: themeV4.palette.text,
      background: themeV4.palette.background,
      divider: themeV4.palette.divider,
    },
    shape: {
      borderRadius: themeV4.shape.borderRadius,
    },
    typography: {
      fontFamily: themeV4.typography.fontFamily,
    }
  }), [themeV4]);

  // States
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Effects
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const exportarGridParaExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: 1,
        pageSize: 9999999,
        onlyRated: onlyRated ? "true" : "false"
      });

      const ticketsData = data.tickets.map(ticket => {
        const createdAt = new Date(ticket.createdAt);
        const closedAt = new Date(ticket.closedAt);
        return {
          id: ticket.id,
          Conexão: ticket.whatsappName,
          Contato: ticket.contactName,
          Usuário: ticket.userName,
          Fila: ticket.queueName,
          Status: ticket.status,
          ÚltimaMensagem: ticket.lastMessage,
          DataAbertura: createdAt.toLocaleDateString() + ' ' + createdAt.toLocaleTimeString(),
          DataFechamento: ticket.closedAt ? closedAt.toLocaleDateString() + ' ' + closedAt.toLocaleTimeString() : "",
          TempoDeAtendimento: ticket.supportTime,
          nps: ticket.NPS,
        }
      });

      const ws = XLSX.utils.json_to_sheet(ticketsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'RelatorioDeAtendimentos');
      XLSX.writeFile(wb, 'relatorio-de-atendimentos.xlsx');

      setPageNumber(pageNumber);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (page) => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: page,
        pageSize: pageSize,
        onlyRated: onlyRated ? "true" : "false"
      });

      setTotalTickets(data.totalTickets.total);
      setHasMore(data.tickets.length === pageSize);
      setTickets(data.tickets);
      setPageNumber(page);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }

  // Handlers
  const handleSelectedUsers = (selecteds) => setUserIds(selecteds.map((t) => t.id));
  const handleSelectedWhatsapps = (selecteds) => setSelectedWhatsapp(selecteds.map((t) => t.id));
  const handleSelectedStatus = (selecteds) => setSelectedStatus(selecteds.map((t) => t.status));

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook": return <Facebook sx={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram": return <Instagram sx={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp": return <WhatsApp sx={{ color: "#25d366", verticalAlign: "middle" }} />;
      default: return "error";
    }
  };

  const renderOption = (props, option) => {
    if (option.number) {
      return (
        <li {...props}>
          {IconChannel(option.channel)}
          <span style={{ marginLeft: 10 }}>{option.name} - {option.number}</span>
        </li>
      );
    }
    return <li {...props}>{i18n.t("newTicketModal.add")} {option.name}</li>;
  };

  const filterOptions = createFilterOptions({ trim: true });

  const createAddContactOption = (options, params) => {
    const filtered = filterOptions(options, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({ name: `${params.inputValue}` });
    }
    return filtered;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        backgroundColor: isEmbedded ? "transparent" : (theme.palette.mode === 'light' ? '#f5f5f5' : 'background.default'),
        minHeight: isEmbedded ? 'auto' : '100%',
        p: isEmbedded ? 0 : 3
      }}>
        {openTicketMessageDialog && (
          <ShowTicketLogModal
            isOpen={openTicketMessageDialog}
            handleClose={() => setOpenTicketMessageDialog(false)}
            ticketId={ticketOpen.id}
          />
        )}

        <Container maxWidth="xl" disableGutters>
          {/* Header */}
          {/* Header - Only if not embedded */}
          {!isEmbedded && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
              <Box>
                <Typography variant="h4" fontWeight="800" color="primary" gutterBottom>
                  {i18n.t("reports.title")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Relatórios detalhados de atendimentos
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 'bold' }}
              >
                {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
              </Button>
            </Box>
          )}

          {/* Filters Area */}
          {showFilters && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: "16px",
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={options}
                    loading={loading}
                    getOptionLabel={(option) => option.number ? `${option.name} - ${option.number}` : option.name}
                    renderOption={renderOption}
                    filterOptions={createAddContactOption}
                    onChange={(e, newValue) => {
                      setSelectedContactId(newValue?.id);
                      setSearchParam("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n.t("newTicketModal.fieldLabel")}
                        variant="outlined"
                        onChange={(e) => setSearchParam(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatusFilter onFiltered={handleSelectedStatus} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <UsersFilter onFiltered={handleSelectedUsers} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <QueueSelectCustom
                    selectedQueueIds={queueIds}
                    onChange={values => setQueueIds(values)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Data Inicial"
                    type="date"
                    value={dateFrom}
                    fullWidth
                    size="small"
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Data Final"
                    type="date"
                    value={dateTo}
                    fullWidth
                    size="small"
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Actions */}
                <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={onlyRated}
                        onChange={() => setOnlyRated(!onlyRated)}
                      />
                    }
                    label={i18n.t("reports.buttons.onlyRated")}
                  />
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SaveAlt />}
                    onClick={exportarGridParaExcel}
                    sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Exportar Excel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleFilter(1)}
                    sx={{ borderRadius: 50, px: 4, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {i18n.t("reports.buttons.filter")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Table Area */}
          {/* Results Area (Cards) */}
          {loading && tickets.length === 0 ? (
            <Box sx={{ width: '100%', mt: 4 }}>
              <LinearProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {tickets.map((ticket) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={ticket.id}>
                  <Card 
                    elevation={0} 
                    sx={{ 
                      height: "100%", 
                      borderRadius: "16px", 
                      border: "1px solid", 
                      borderColor: "divider",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                        borderColor: "primary.light"
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: "bold" }}>
                            TICKET #{ticket.id}
                          </Typography>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {ticket.contactName}
                          </Typography>
                        </Box>
                        <Chip 
                          label={ticket.status === 'closed' ? "Concluído" : "Aberto"} 
                          size="small"
                          sx={{ 
                            bgcolor: ticket.status === 'closed' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                            color: ticket.status === 'closed' ? 'success.main' : 'warning.main',
                            fontWeight: "bold",
                            borderRadius: "8px"
                          }}
                        />
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Técnico / Fila</Typography>
                          <Typography variant="body2" fontWeight="500">
                             {ticket.userName || "Sem técnico"} • <Box component="span" sx={{ color: "primary.main" }}>{ticket.queueName || "Sem fila"}</Box>
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="textSecondary">Última Mensagem</Typography>
                          <Typography variant="body2" noWrap sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            {ticket.lastMessage || "Sem mensagens"}
                          </Typography>
                        </Box>

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary">Abertura</Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{ticket.createdAt}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary">T. Atendimento</Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>{ticket.supportTime || "00:00"}</Typography>
                          </Grid>
                        </Grid>

                        {ticket.NPS && (
                          <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 1, borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="primary" fontWeight="bold">Nota NPS: {ticket.NPS}</Typography>
                          </Box>
                        )}
                      </Stack>

                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
                        <Tooltip title="Logs do Ticket">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setOpenTicketMessageDialog(true)
                              setTicketOpen(ticket)
                            }}
                            sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                          >
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Acessar Ticket">
                          <IconButton
                            size="small"
                            onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                            sx={{ color: 'success.main', bgcolor: alpha(theme.palette.success.main, 0.1) }}
                          >
                            <Forward fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              
              {tickets.length === 0 && !loading && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 5, textAlign: "center", borderRadius: "16px", bgcolor: "transparent", border: "1px dashed", borderColor: "divider" }}>
                    <Typography color="textSecondary">Nenhum registro encontrado para os filtros selecionados.</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}

          {/* Pagination Area */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="textSecondary">Itens por página:</Typography>
              <TextField
                select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                SelectProps={{ native: true, style: { fontWeight: 'bold' } }}
              >
                <option value={8}>8</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
              </TextField>
            </Box>
            <Pagination
              count={Math.ceil(totalTickets / pageSize) || 1}
              page={pageNumber}
              onChange={(event, value) => handleFilter(value)}
              color="primary"
              shape="rounded"
              size="large"
            />
          </Box>

        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Reports;
